import React from 'react';
import { useSelector } from 'react-redux';
import { OpTable, IOpTableProps } from 'components/customAntd/DLS/OpTable/OpTable';
import { OpTableRawColumnType } from 'components/customAntd/DLS/OpTableCore/OpTableCore';
import { RootState } from 'store/store';
import { TABLE_HEIGHT } from 'constants/ui';
import { formatFullName, hasPermission } from 'utils/utils';
import { StopOutlined, EyeOutlined } from "@ant-design/icons";
import { RedFlag } from 'types/redFlagTypes';
import dayjs from 'dayjs';
import { DATE_FORMAT } from 'constants/dates';
import { useTranslation } from 'react-i18next';

interface RedFlagsTableProps {
    redFlags: RedFlag[];
    loading: boolean;
    handleEditClick: (redFlag: RedFlag) => void;
    handleAddClick: () => void;
}

const RedFlagsTable: React.FC<RedFlagsTableProps> = ({ redFlags, loading, handleEditClick, handleAddClick }) => {
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);
    const hasRedflagWrite = hasPermission(tokenScopeList, orgId, 'o', 'redflagconf:w');

    const { t } = useTranslation();

    const transformedRedFlags = redFlags.map((item: RedFlag) => ({
        ...item,
        // levelName:
        //     item.levelName === 'Blocklist'
        //         ? t('redFlag.table.blocklist')
        //         : item.levelName === 'Watchlist'
        //             ? t('redFlag.table.watchlist')
        //             : item.levelName,
        dateOfBirth: dayjs(item.dateOfBirth).isValid() ? dayjs(item.dateOfBirth).format(DATE_FORMAT) : null,
        fullName: formatFullName(item.firstName, item.middleName, item.lastName),
    }));

    const columns: OpTableRawColumnType[] = [
        {
            dataIndex: 'levelName',
            label: t('redFlag.table.type'),
            width: 300,
            render: (text: string) => {
                if (text === 'Blocklist') {
                    return <span><StopOutlined style={{ color: 'rgba(242,103,87,1.0)' }} /> {t('redFlag.table.blocklist')}</span>;
                } else if (text === 'Watchlist') {
                    return <span><EyeOutlined style={{ color: '#FFD54F' }} /> {t('redFlag.table.watchlist')}</span>;
                }
                return text;
            },
            filter: {
                type: 'multiSelect',
                options: [
                    { label: t('redFlag.table.blocklist'), value: 'Blocklist' },
                    { label: t('redFlag.table.watchlist'), value: 'Watchlist' }
                ],
            },
            defaultFilteredValue: ['Blocklist', 'Watchlist'],
            sorter: (a, b) => (a.levelName || '').localeCompare(b.levelName || ''),
        },
        {
            dataIndex: 'fullName',
            label: t('redFlag.table.fullName'),
            filter: {
                type: 'input',
            },
            sorter: (a, b) => (a.fullName || '').localeCompare(b.fullName || ''),
        },
        {
            dataIndex: 'dateOfBirth',
            label: t('redFlag.table.dateOfBirth'),
            hidden: true,
            filter: {
                type: 'input',
            },
            sorter: (a, b) => (a.dateOfBirth || '').localeCompare(b.dateOfBirth || ''),
        },
        {
            dataIndex: 'identification',
            label: t('redFlag.table.identification'),
            hidden: true,
            filter: {
                type: 'input',
            },
            sorter: (a, b) => (a.identification || '').localeCompare(b.identification || ''),
        }
    ];

    const opTableProps: IOpTableProps = {
        dataSource: transformedRedFlags,
        columns: columns,
        rowActions: {
            onEditClick: (redFlag: RedFlag) => handleEditClick(redFlag),
        },
        height: TABLE_HEIGHT,
        allowGlobalSearch: true,
        loading: loading,
        writeAccess: hasRedflagWrite,
        allowAddition: hasRedflagWrite ? {
            itemName: t('redFlag.itemName'),
            onClick: handleAddClick,
        } : false,
        allowExport: true,
        allowShowHideColumns: true,
    };

    return <OpTable {...opTableProps} />;
};

export default RedFlagsTable;
